import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProviderCalendarView from './ProviderCalendarView';
import StaffTimelineBoard from './StaffTimelineBoard';
import ProviderAddOns from './ProviderAddOns';
import ProviderRevenueDashboard from './ProviderRevenueDashboard';
import AddToCalendar from './AddToCalendar';
import CalendarExportToolbar from './CalendarExportToolbar';
import MyClients from './MyClients';
import ClientImport from './ClientImport';
import StaffPerformanceModal from './StaffPerformanceModal';
import StaffDaysOffModal from './StaffDaysOffModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enhanced Staff Schedule Modal Component with Visual Timeline
const StaffScheduleModal = ({ staff, schedule, onSave, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [localSchedule, setLocalSchedule] = useState(schedule);
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const [selectedDay, setSelectedDay] = useState('sunday');
  const [newSlot, setNewSlot] = useState({ start: '09:00', end: '17:00', type: 'work' });
  const [showQuickPresets, setShowQuickPresets] = useState(false);

  // Generate time slots for visual timeline (6 AM to 10 PM in 30-min intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 22) slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Check if a time slot is within working hours
  const isTimeInSlot = (time, slot) => {
    const timeMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    const startMinutes = parseInt(slot.start.split(':')[0]) * 60 + parseInt(slot.start.split(':')[1]);
    const endMinutes = parseInt(slot.end.split(':')[0]) * 60 + parseInt(slot.end.split(':')[1]);
    return timeMinutes >= startMinutes && timeMinutes < endMinutes;
  };

  // Get slot status for a given time
  const getSlotStatus = (time, day) => {
    const daySchedule = localSchedule[day] || [];
    for (let slot of daySchedule) {
      if (isTimeInSlot(time, slot)) {
        return slot.type || 'work';
      }
    }
    return 'off';
  };

  const addTimeSlot = () => {
    const daySchedule = localSchedule[selectedDay] || [];
    
    // Validate times
    const startMinutes = parseInt(newSlot.start.split(':')[0]) * 60 + parseInt(newSlot.start.split(':')[1]);
    const endMinutes = parseInt(newSlot.end.split(':')[0]) * 60 + parseInt(newSlot.end.split(':')[1]);
    
    if (startMinutes >= endMinutes) {
      alert('End time must be after start time');
      return;
    }
    
    // Check for overlaps
    const hasOverlap = daySchedule.some(slot => {
      const slotStart = parseInt(slot.start.split(':')[0]) * 60 + parseInt(slot.start.split(':')[1]);
      const slotEnd = parseInt(slot.end.split(':')[0]) * 60 + parseInt(slot.end.split(':')[1]);
      return (startMinutes < slotEnd && endMinutes > slotStart);
    });
    
    if (hasOverlap) {
      alert('This time slot overlaps with an existing slot');
      return;
    }
    
    setLocalSchedule({
      ...localSchedule,
      [selectedDay]: [...daySchedule, { ...newSlot }].sort((a, b) => a.start.localeCompare(b.start))
    });
  };

  const removeTimeSlot = (day, index) => {
    const daySchedule = localSchedule[day] || [];
    setLocalSchedule({
      ...localSchedule,
      [day]: daySchedule.filter((_, i) => i !== index)
    });
  };

  // Quick preset schedules
  const applyPreset = (presetType) => {
    let preset = {};
    days.forEach(day => {
      switch(presetType) {
        case 'full-time':
          preset[day] = [{ start: '09:00', end: '17:00', type: 'work' }];
          break;
        case 'split-shift':
          preset[day] = [
            { start: '09:00', end: '13:00', type: 'work' },
            { start: '14:00', end: '18:00', type: 'work' }
          ];
          break;
        case 'morning-shift':
          preset[day] = [{ start: '08:00', end: '14:00', type: 'work' }];
          break;
        case 'evening-shift':
          preset[day] = [{ start: '14:00', end: '22:00', type: 'work' }];
          break;
        default:
          preset[day] = [];
      }
    });
    setLocalSchedule(preset);
    setShowQuickPresets(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{t('providerDashboard.scheduleFor')} {staff.name}</h3>
          <button
            onClick={() => setShowQuickPresets(!showQuickPresets)}
            className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
          >
            📋 Quick Presets
          </button>
        </div>

        {/* Quick Presets */}
        {showQuickPresets && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium mb-3 text-sm">Apply Quick Schedule:</h4>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => applyPreset('full-time')} className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-sm">
                Full Time (9 AM - 5 PM)
              </button>
              <button onClick={() => applyPreset('split-shift')} className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-sm">
                Split Shift (9-1, 2-6)
              </button>
              <button onClick={() => applyPreset('morning-shift')} className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-sm">
                Morning (8 AM - 2 PM)
              </button>
              <button onClick={() => applyPreset('evening-shift')} className="px-3 py-2 bg-white border rounded hover:bg-gray-50 text-sm">
                Evening (2 PM - 10 PM)
              </button>
            </div>
          </div>
        )}
        
        {/* Day selector with tabs */}
        <div className="mb-4 border-b">
          <div className="flex space-x-2 overflow-x-auto">
            {days.map(day => (
              <button
                key={day}
                onClick={() => setSelectedDay(day)}
                className={`px-4 py-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                  selectedDay === day
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(`providerDashboard.${day}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Visual Timeline */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-3 text-sm">Visual Timeline for {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}</h4>
          <div className="overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {timeSlots.map(time => {
                const status = getSlotStatus(time, selectedDay);
                return (
                  <div key={time} className="flex flex-col items-center">
                    <div className={`w-8 h-16 rounded ${
                      status === 'work' ? 'bg-green-500' :
                      status === 'break' ? 'bg-yellow-500' :
                      'bg-gray-200'
                    }`} title={`${time} - ${status}`} />
                    {time.endsWith(':00') && (
                      <span className="text-xs mt-1">{time.split(':')[0]}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-3 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-500 rounded"></div> Working
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div> Break
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-gray-200 rounded"></div> Off
              </span>
            </div>
          </div>
        </div>

        {/* Add time slot */}
        <div className="mb-4 p-4 bg-white border rounded-lg">
          <h4 className="font-medium mb-3 text-sm">Add Shift/Break</h4>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Start Time</label>
              <input
                type="time"
                value={newSlot.start}
                onChange={(e) => setNewSlot({ ...newSlot, start: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">End Time</label>
              <input
                type="time"
                value={newSlot.end}
                onChange={(e) => setNewSlot({ ...newSlot, end: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Type</label>
              <select
                value={newSlot.type}
                onChange={(e) => setNewSlot({ ...newSlot, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="work">Work</option>
                <option value="break">Break</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={addTimeSlot}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                + Add
              </button>
            </div>
          </div>
        </div>

        {/* Current slots for selected day */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-2 text-sm">Scheduled Slots for {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}</h4>
          {localSchedule[selectedDay] && localSchedule[selectedDay].length > 0 ? (
            <div className="space-y-2">
              {localSchedule[selectedDay].map((slot, index) => (
                <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      slot.type === 'work' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {slot.type === 'work' ? '💼 Work' : '☕ Break'}
                    </span>
                    <span className="text-sm">{slot.start} - {slot.end}</span>
                  </div>
                  <button
                    onClick={() => removeTimeSlot(selectedDay, index)}
                    className="text-red-600 hover:text-red-800 text-xs px-2"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-500">{t('providerDashboard.noSlotsScheduled')}</span>
          )}
        </div>

        {/* Weekly overview */}
        <div className="mb-4">
          <h4 className="font-medium mb-2 text-sm">Weekly Overview</h4>
          <div className="grid grid-cols-7 gap-2">
            {days.map(day => (
              <div key={day} className="border rounded p-2 text-xs">
                <div className="font-medium mb-1 capitalize">{day.slice(0, 3)}</div>
                {localSchedule[day] && localSchedule[day].length > 0 ? (
                  localSchedule[day].map((slot, idx) => (
                    <div key={idx} className={`text-xs mb-1 px-1 py-0.5 rounded ${
                      slot.type === 'work' ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                      {slot.start}-{slot.end}
                    </div>
                  ))
                ) : (
                  <span className="text-gray-400">Off</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => onSave(localSchedule)}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
          >
            Save Schedule
          </button>
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const ImprovedProviderDashboard = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, sessionToken } = useUnifiedAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get('tab') || 'calendar'
  );
  const [scheduleView, setScheduleView] = useState('board');
  const [loading, setLoading] = useState(false);
  const [providerId, setProviderId] = useState(null); // Store actual provider ID
  
  // Profile State
  const [profileData, setProfileData] = useState({
    name: '',
    description: '',
    tags: [],
    profile_picture: '',
    gallery_images: [],
    address: '',
    area: '',
    governorate: '',
    google_maps_link: '',
    phone: '',
    email: ''
  });
  const [newTag, setNewTag] = useState('');
  
  // Services State
  const [services, setServices] = useState([]);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: '',
    duration: 30,
    price: 0,
    description: '',
    assigned_staff: []
  });
  
  // Staff State
  const [staff, setStaff] = useState([]);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '' });
  const [selectedStaffForSchedule, setSelectedStaffForSchedule] = useState(null);
  const [staffSchedules, setStaffSchedules] = useState({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedStaffForDaysOff, setSelectedStaffForDaysOff] = useState(null);
  const [selectedStaffForPerformance, setSelectedStaffForPerformance] = useState(null);
  
  // Appointments State
  const [appointments, setAppointments] = useState([]);
  const [appointmentFilter, setAppointmentFilter] = useState('upcoming'); // upcoming, completed, cancelled
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '', staffId: '' });
  const [rescheduleAvailableSlots, setRescheduleAvailableSlots] = useState([]);
  const [loadingRescheduleSlots, setLoadingRescheduleSlots] = useState(false);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  
  // Subscription State
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  useEffect(() => {
    if (user && sessionToken) {
      fetchProviderData();
      fetchSubscriptionData();
    }
  }, [user, sessionToken]);
  
  const fetchSubscriptionData = async () => {
    try {
      setLoadingSubscription(true);
      console.log('Fetching subscription data...');
      const response = await axios.get(`${API}/provider/my-subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      console.log('Subscription response:', response.data);
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error.response?.data || error.message);
      // Set a default subscription object so the UI doesn't break
      setSubscriptionData({
        subscription: {
          current_tier: 'Free',
          commission_rate: 15,
          expires_at: null,
          days_remaining: null,
          is_expired: false,
          is_active: false,
          limits: { max_staff: 5, max_services: 10, booking_window_months: 3 }
        },
        tier_history: [],
        upgrade_options: [],
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setLoadingSubscription(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!window.confirm(t('providerDashboard.subscription.confirmCancel', 'Are you sure you want to cancel your subscription? You will be downgraded to the Free tier.'))) {
      return;
    }
    
    try {
      setCancellingSubscription(true);
      await axios.post(`${API}/provider/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      // Refresh subscription data
      await fetchSubscriptionData();
      alert(t('providerDashboard.subscription.cancelSuccess', 'Subscription cancelled successfully.'));
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(t('providerDashboard.subscription.cancelError', 'Failed to cancel subscription. Please try again.'));
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchProviderData = async () => {
    try {
      // Use my-provider-profile endpoint to get the correct provider data
      const response = await axios.get(`${API}/my-provider-profile`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data && response.data.profile) {
        const provider = response.data.profile;
        // Store the provider ID for future API calls
        setProviderId(provider.id);
        
        setProfileData({
          name: provider.name || '',
          description: provider.description || '',
          tags: provider.tags || [],
          profile_picture: provider.profile_images?.[0] || '',
          gallery_images: provider.profile_images || [],
          address: provider.address || '',
          area: provider.area || '',
          governorate: provider.governorate || '',
          google_maps_link: provider.google_maps_link || '',
          phone: provider.phone || '',
          email: provider.email || ''
        });
        // Don't use provider.services - fetch from services collection instead
        setStaff(provider.staff || []);
        setStaffSchedules(provider.staff_schedules || {});
        
        // Fetch services from services collection
        fetchServices(provider.id);
        
        // Fetch subscription limits
        fetchSubscriptionLimits(provider.id);
        
        // Fetch appointments for this provider
        fetchAppointments(provider.id);
      }
    } catch (error) {
      console.error('Error fetching provider data:', error);
    }
  };
  
  const fetchServices = async (provider_id) => {
    try {
      const response = await axios.get(`${API}/providers/${provider_id}/services`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data) {
        setServices(response.data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    }
  };
  
  const fetchSubscriptionLimits = async (provider_id) => {
    try {
      const response = await axios.get(`${API}/providers/${provider_id}/check_limits`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data) {
        setSubscriptionLimits(response.data);
      }
    } catch (error) {
      console.error('Error fetching subscription limits:', error);
    }
  };
  
  const fetchAppointments = async (provider_id) => {
    try {
      const response = await axios.get(`${API}/provider/appointments`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const saveProfile = async () => {
    if (!providerId) {
      alert('Provider ID not found. Please refresh the page.');
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/healthcare-providers/${providerId}`,
        {
          name: profileData.name,
          description: profileData.description,
          tags: profileData.tags,
          profile_images: profileData.gallery_images,
          address: profileData.address,
          area: profileData.area,
          governorate: profileData.governorate,
          google_maps_link: profileData.google_maps_link,
          phone: profileData.phone,
          email: profileData.email
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      alert('✅ Profile updated successfully!');
      // Refresh the data to show updated values
      await fetchProviderData();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('❌ Failed to update profile');
    }
    setLoading(false);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !profileData.tags.includes(newTag.trim())) {
      setProfileData({
        ...profileData,
        tags: [...profileData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setProfileData({
      ...profileData,
      tags: profileData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only JPG, JPEG, PNG
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      alert('Please upload only JPG, JPEG, or PNG images');
      return;
    }

    // Validate file size (6MB max)
    if (file.size > 6 * 1024 * 1024) {
      alert('File size must be less than 6MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', type);  // Send 'profile' or 'gallery'

    try {
      const response = await axios.post(`${API}/profile/upload-picture`, formData, {
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;

      if (type === 'profile') {
        // Profile picture - only one allowed
        setProfileData({
          ...profileData,
          profile_picture: imageUrl
        });
      } else {
        // Gallery images - max 6
        if (profileData.gallery_images.length >= 6) {
          alert('Maximum 6 gallery images allowed');
          return;
        }
        setProfileData({
          ...profileData,
          gallery_images: [...profileData.gallery_images, imageUrl]
        });
      }
      
      alert('✅ Image uploaded successfully!');
      // Refresh to show the new image
      await fetchProviderData();
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert('❌ Failed to upload image: ' + errorMessage);
    }
  };

  const handleDeleteImage = (imageUrl) => {
    setProfileData({
      ...profileData,
      gallery_images: profileData.gallery_images.filter(img => img !== imageUrl),
      profile_picture: profileData.profile_picture === imageUrl ? (profileData.gallery_images[1] || '') : profileData.profile_picture
    });
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) {
      alert('Please enter a service name');
      return;
    }

    try {
      // Use the dedicated service creation endpoint
      await axios.post(
        `${API}/providers/${providerId}/services`,
        newService,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh services from database
      await fetchServices(providerId);
      setShowAddServiceModal(false);
      setNewService({ name: '', duration: 30, price: 0, description: '', assigned_staff: [] });
      alert('✅ Service added successfully!');
    } catch (error) {
      console.error('Error adding service:', error);
      const detail = error.response?.data?.detail;
      let errorMessage;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail && typeof detail === 'object') {
        // Backend sometimes returns structured detail: {error, message, ...}
        errorMessage = detail.message || detail.error || JSON.stringify(detail);
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else {
        errorMessage = error.message || 'Unknown error';
      }
      alert('❌ Failed to add service: ' + errorMessage);
    }
  };

  const handleRemoveService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to remove this service?')) return;

    try {
      // Use the delete service endpoint
      await axios.delete(
        `${API}/providers/${providerId}/services/${serviceId}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh services from database
      await fetchServices(providerId);
      alert('✅ Service removed successfully!');
    } catch (error) {
      console.error('Error removing service:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Failed to remove service: ${errorMessage}`);
    }
  };
  
  const handleEditService = (service) => {
    setEditingService(service);
    setShowEditServiceModal(true);
  };
  
  const handleUpdateService = async () => {
    if (!editingService.name.trim()) {
      alert('Please enter a service name');
      return;
    }

    try {
      // Update service via API
      await axios.put(
        `${API}/providers/${providerId}/services/${editingService.id}`,
        {
          name: editingService.name,
          description: editingService.description,
          duration: editingService.duration,
          price: editingService.price,
          assigned_staff: editingService.assigned_staff
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh services from database
      await fetchServices(providerId);
      setShowEditServiceModal(false);
      setEditingService(null);
      alert('✅ Service updated successfully!');
    } catch (error) {
      console.error('Error updating service:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert('❌ Failed to update service: ' + errorMessage);
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.name.trim()) {
      alert('Please enter a staff name');
      return;
    }

    try {
      // Use the correct POST endpoint to add staff
      const response = await axios.post(
        `${API}/providers/${providerId}/staff`,
        {
          name: newStaff.name.trim(),
          email: null,
          phone: null,
          specialties: [],
          hourly_rate: null,
          bio: ''
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // The response contains the newly created staff member
      const newStaffMember = response.data;
      
      // Update local state with the new staff member
      setStaff([...staff, newStaffMember]);

      // Auto-link the new staff to every existing service so bookings work
      // immediately. Provider can unlink specific services later if needed.
      if (services && services.length > 0) {
        try {
          await Promise.all(services.map(svc => {
            const existingAssigned = svc.assigned_staff || [];
            if (existingAssigned.includes(newStaffMember.id)) return Promise.resolve();
            return axios.put(
              `${API}/providers/${providerId}/services/${svc.id}`,
              { ...svc, assigned_staff: [...existingAssigned, newStaffMember.id] },
              { headers: { Authorization: `Bearer ${sessionToken}` } }
            );
          }));
          await fetchServices(providerId);
        } catch (e) {
          console.warn('Could not auto-assign new staff to existing services:', e);
        }
      }
      
      // Initialize empty schedule for new staff member
      const updatedSchedules = {
        ...staffSchedules,
        [newStaffMember.id]: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      };
      setStaffSchedules(updatedSchedules);
      
      // Reset form and close modal
      setNewStaff({ name: '' });
      setShowAddStaffModal(false);
      alert(`✅ Staff member "${newStaffMember.name}" added successfully!`);
    } catch (error) {
      console.error('Error adding staff:', error);
      const errorMessage = error.response?.data?.detail?.message || error.response?.data?.detail || error.message || 'Unknown error';
      
      // Check if it's a subscription limit error
      if (error.response?.data?.detail?.error === 'staff_limit_reached') {
        alert(`⚠️ Staff Limit Reached\n\n${errorMessage}`);
      } else {
        alert(`❌ Failed to add staff member: ${errorMessage}`);
      }
    }
  };

  const saveStaffSchedule = async (staffId, schedule) => {
    try {
      const updatedSchedules = {
        ...staffSchedules,
        [staffId]: schedule
      };

      await axios.put(
        `${API}/healthcare-providers/${providerId}`,
        { staff_schedules: updatedSchedules },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      setStaffSchedules(updatedSchedules);
      setShowScheduleModal(false);
      alert('✅ Schedule saved successfully!');
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('❌ Failed to save schedule');
    }
  };

  const handleRemoveStaff = async (staffId) => {
    // Find staff member name for confirmation message
    const staffMember = staff.find(s => s.id === staffId);
    const staffName = staffMember?.name || 'this staff member';
    
    if (!window.confirm(`Are you sure you want to remove "${staffName}"?\n\nThis will:\n• Remove their schedule\n• Remove them from all services\n• Delete all their availability data`)) return;

    try {
      // Use the correct DELETE endpoint
      await axios.delete(
        `${API}/providers/${providerId}/staff/${staffId}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Update local state by removing the staff member
      setStaff(staff.filter(s => s.id !== staffId));
      
      // Also update services to remove this staff from assigned_staff arrays
      setServices(services.map(service => ({
        ...service,
        assigned_staff: (service.assigned_staff || []).filter(id => id !== staffId)
      })));
      
      alert(`✅ Staff member "${staffName}" removed successfully!`);
    } catch (error) {
      console.error('Error removing staff:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Failed to remove staff member: ${errorMessage}`);
    }
  };
  
  const handleCompleteAppointment = async (appointmentId) => {
    if (!window.confirm('Mark this appointment as completed?')) return;
    
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh appointments
      if (providerId) {
        fetchAppointments(providerId);
      }
      alert('✅ Appointment marked as completed');
    } catch (error) {
      console.error('Error completing appointment:', error);
      alert('❌ Failed to complete appointment');
    }
  };
  
  const handleCancelAppointment = async (appointmentId) => {
    const reason = window.prompt('Please enter cancellation reason:');
    if (!reason) return;
    
    try {
      await axios.post(
        `${API}/appointments/${appointmentId}/cancel`,
        { cancellation_reason: reason },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh appointments
      if (providerId) {
        fetchAppointments(providerId);
      }
      alert('✅ Appointment cancelled');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert('❌ Failed to cancel appointment: ' + errorMessage);
    }
  };
  
  const handleRescheduleAppointment = async (appointmentId, newDate, newTime, newStaffId) => {
    try {
      // Use POST endpoint with body for provider reschedule
      const response = await axios.post(
        `${API}/appointments/${appointmentId}/reschedule`,
        { 
          new_date: newDate, 
          new_time: newTime,
          new_staff_id: newStaffId
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      // Refresh appointments
      if (providerId) {
        fetchAppointments(providerId);
      }
      setShowRescheduleModal(false);
      setRescheduleAppointment(null);
      setRescheduleAvailableSlots([]);
      
      let successMessage = '✅ Appointment rescheduled successfully';
      if (response.data?.staff_changed) {
        successMessage += ' with a different staff member';
      }
      alert(successMessage);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' ? errorDetail.message : (errorDetail || error.message || 'Unknown error');
      alert('❌ Failed to reschedule appointment: ' + errorMessage);
    }
  };
  
  const openRescheduleModal = (appointment) => {
    setRescheduleAppointment(appointment);
    setRescheduleData({
      date: appointment.appointment_date,
      time: appointment.appointment_time,
      staffId: appointment.staff_id || ''
    });
    setRescheduleAvailableSlots([]);
    setShowRescheduleModal(true);
    // Fetch available slots for the current date and staff
    if (appointment.staff_id && appointment.appointment_date) {
      fetchRescheduleAvailableSlots(appointment.appointment_date, appointment.staff_id, appointment.service_id, appointment.id);
    }
  };

  // Function to get available time slots for rescheduling
  const fetchRescheduleAvailableSlots = async (date, staffId, serviceId, excludeAppointmentId) => {
    if (!date || !staffId) {
      setRescheduleAvailableSlots([]);
      return;
    }

    setLoadingRescheduleSlots(true);
    try {
      // Get the staff member's schedule
      const staffMember = staff.find(s => s.id === staffId);
      if (!staffMember) {
        setRescheduleAvailableSlots([]);
        setLoadingRescheduleSlots(false);
        return;
      }

      // Get day of week
      const dateObj = new Date(date);
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayOfWeek = dayNames[dateObj.getDay()];

      // Get staff schedule for this day
      const staffSchedule = staffSchedules[staffId];
      const daySchedule = staffSchedule?.[dayOfWeek];
      
      // Handle both schedule formats:
      // Format 1: { enabled: true, start: "09:00", end: "17:00" }
      // Format 2: [{ start: "09:00", end: "17:00", type: "work" }]
      let workPeriods = [];
      
      if (Array.isArray(daySchedule)) {
        // Format 2: Array of time blocks
        workPeriods = daySchedule.filter(block => block.type === 'work' || !block.type);
      } else if (daySchedule?.enabled) {
        // Format 1: Single schedule object
        workPeriods = [{ start: daySchedule.start, end: daySchedule.end }];
      }
      
      if (workPeriods.length === 0) {
        setRescheduleAvailableSlots([]);
        setLoadingRescheduleSlots(false);
        return;
      }

      // Get the service to know duration
      const service = services.find(s => s.id === serviceId);
      const duration = service?.duration || 30;

      // Generate all possible slots based on staff working hours
      const slots = [];
      
      for (const period of workPeriods) {
        const [startHour, startMin] = period.start.split(':').map(Number);
        const [endHour, endMin] = period.end.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        for (let time = startMinutes; time + duration <= endMinutes; time += 30) {
          const hours = Math.floor(time / 60);
          const mins = time % 60;
          const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
          if (!slots.includes(timeStr)) {
            slots.push(timeStr);
          }
        }
      }
      
      // Sort slots chronologically
      slots.sort();

      // Get existing appointments for this staff on this date (excluding the appointment being rescheduled)
      const existingAppointments = appointments.filter(apt => 
        apt.staff_id === staffId && 
        apt.appointment_date === date && 
        apt.id !== excludeAppointmentId &&
        ['confirmed', 'pending'].includes(apt.status)
      );

      // Filter out slots that conflict with existing appointments
      const availableSlots = slots.filter(slot => {
        const slotMinutes = parseInt(slot.split(':')[0]) * 60 + parseInt(slot.split(':')[1]);
        const slotEnd = slotMinutes + duration;

        return !existingAppointments.some(apt => {
          const aptStart = parseInt(apt.appointment_time.split(':')[0]) * 60 + parseInt(apt.appointment_time.split(':')[1]);
          const aptEnd = aptStart + (apt.duration || 30);
          // Check for overlap
          return (slotMinutes < aptEnd && slotEnd > aptStart);
        });
      });

      // Filter out past times if date is today
      const today = new Date().toISOString().split('T')[0];
      const finalSlots = date === today 
        ? availableSlots.filter(slot => {
            const now = new Date();
            const [h, m] = slot.split(':').map(Number);
            return h > now.getHours() || (h === now.getHours() && m > now.getMinutes());
          })
        : availableSlots;

      setRescheduleAvailableSlots(finalSlots);
    } catch (error) {
      console.error('Error calculating available slots:', error);
      setRescheduleAvailableSlots([]);
    }
    setLoadingRescheduleSlots(false);
  };

  // Update available slots when date or staff changes in reschedule modal
  const handleRescheduleDateChange = (newDate) => {
    setRescheduleData(prev => ({ ...prev, date: newDate, time: '' }));
    if (rescheduleAppointment && newDate && rescheduleData.staffId) {
      fetchRescheduleAvailableSlots(newDate, rescheduleData.staffId, rescheduleAppointment.service_id, rescheduleAppointment.id);
    }
  };

  const handleRescheduleStaffChange = (newStaffId) => {
    setRescheduleData(prev => ({ ...prev, staffId: newStaffId, time: '' }));
    if (rescheduleAppointment && rescheduleData.date && newStaffId) {
      fetchRescheduleAvailableSlots(rescheduleData.date, newStaffId, rescheduleAppointment.service_id, rescheduleAppointment.id);
    }
  };

  if (!user || user.user_type !== 'provider') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Access denied. Providers only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('providerDashboard.title')}</h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex gap-8 px-6 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => { setActiveTab('calendar'); setScheduleView('board'); }}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'calendar'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.schedule')}
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'appointments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.appointments')}
              </button>
              <button
                onClick={() => setActiveTab('clients')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'clients'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.clients')}
              </button>
              <button
                onClick={() => setActiveTab('revenue')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'revenue'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.revenue')}
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'services'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.services')}
              </button>
              <button
                onClick={() => setActiveTab('staff')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'staff'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.staff')}
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'subscription'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('providerDashboard.subscriptionLabel', 'Subscription')}
              </button>
            </nav>
          </div>
        </div>

        {/* Schedule Tab — day board first, month calendar on demand */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            <div className="flex gap-2 print:hidden">
              <button
                onClick={() => setScheduleView('board')}
                data-testid="schedule-view-board-btn"
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  scheduleView === 'board' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t('providerDashboard.dayBoard', 'Day Board')}
              </button>
              <button
                onClick={() => setScheduleView('month')}
                data-testid="schedule-view-month-btn"
                className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  scheduleView === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t('providerDashboard.monthView', 'Month')}
              </button>
            </div>
            {scheduleView === 'board' ? (
              <StaffTimelineBoard embedded />
            ) : (
              <ProviderCalendarView
                appointments={appointments}
                services={services}
                staff={staff}
                staffSchedules={staffSchedules}
                onReschedule={handleRescheduleAppointment}
                onCancel={handleCancelAppointment}
              />
            )}
          </div>
        )}

        {/* Revenue & Billing Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                onClick={() => navigate('/provider/walk-ins')}
                data-testid="walk-in-earnings-link"
                className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-start hover:bg-amber-100"
              >
                <span>
                  <span className="block text-sm font-bold text-amber-900">{t('walkIn.earningsTitle', 'Walk-in earnings')}</span>
                  <span className="block text-xs text-amber-700">{t('walkIn.earningsHint', 'Logged in person · no EnayaHub commission')}</span>
                </span>
                <span className="text-lg text-amber-700">→</span>
              </button>
              <button
                onClick={() => navigate('/provider/staff-earnings')}
                data-testid="staff-earnings-link"
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-start hover:bg-slate-50"
              >
                <span>
                  <span className="block text-sm font-bold text-slate-900">{t('staffEarnings.title', 'Staff earnings')}</span>
                  <span className="block text-xs text-slate-500">{t('staffEarnings.hint', 'This month, online and walk-in')}</span>
                </span>
                <span className="text-lg text-slate-400">→</span>
              </button>
            </div>
            <ProviderRevenueDashboard />
          </div>
        )}

        {/* My Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-6">
            <ClientImport onComplete={() => { /* MyClients refreshes on next mount */ }} />
            <div className="bg-white rounded-lg shadow p-6">
              <MyClients />
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">{t('providerDashboard.services')}</h2>
              <div className="flex items-center gap-3">
                {subscriptionLimits && (
                  <span className="text-sm text-gray-600">
                    {services.length}/{subscriptionLimits.services_limit === -1 ? '∞' : subscriptionLimits.services_limit} Services
                  </span>
                )}
                <button
                  onClick={() => {
                    if (subscriptionLimits && services.length >= subscriptionLimits.services_limit && subscriptionLimits.services_limit !== -1) {
                      alert(`⚠️ Service Limit Reached\n\nYou've reached your ${subscriptionLimits.tier_name} tier limit of ${subscriptionLimits.services_limit} services.\n\nUpgrade your subscription to add more services!`);
                      navigate('/pricing');
                    } else {
                      // Pre-assign all existing staff so the service is bookable immediately.
                      // Provider can uncheck specific ones in the modal if a staff doesn't do this service.
                      setNewService({
                        name: '',
                        duration: 30,
                        price: 0,
                        description: '',
                        assigned_staff: (staff || []).map(s => s.id)
                      });
                      setShowAddServiceModal(true);
                    }
                  }}
                  disabled={subscriptionLimits && services.length >= subscriptionLimits.services_limit && subscriptionLimits.services_limit !== -1}
                  className={`px-4 py-2 rounded-lg ${
                    subscriptionLimits && services.length >= subscriptionLimits.services_limit && subscriptionLimits.services_limit !== -1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  + {t('providerDashboard.addService')}
                </button>
              </div>
            </div>

            <div className="grid gap-4">
              {services.map((service) => (
                <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{service.name}</h3>
                      <p className="text-gray-600 text-sm mt-1">{service.description}</p>
                      <div className="flex gap-4 mt-2">
                        <span className="text-sm text-gray-500">
                          {t('providerDashboard.serviceDuration')}: {service.duration} {t('providerDashboard.minutes')}
                        </span>
                        <span className="text-sm text-gray-500">
                          {t('providerDashboard.servicePrice')}: KD {service.price}
                        </span>
                      </div>
                      {service.assigned_staff && service.assigned_staff.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">👥 Assigned Staff: </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {service.assigned_staff.map(staffId => {
                              const staffMember = staff.find(s => s.id === staffId);
                              return staffMember ? (
                                <span key={staffId} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {staffMember.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditService(service)}
                        className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 rounded text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemoveService(service.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ProviderAddOns />

            {/* Add Service Modal */}
            {showAddServiceModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-xl font-semibold mb-4">Add New Service</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Name *
                      </label>
                      <input
                        type="text"
                        value={newService.name}
                        onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Deep Tissue Massage"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={newService.description}
                        onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Describe the service..."
                      />
                    </div>
                    
                    {/* Staff Assignment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Staff (Optional)
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Select which staff members can perform this service</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {staff.length > 0 ? (
                          staff.map((member) => (
                            <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newService.assigned_staff.includes(member.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewService({
                                      ...newService,
                                      assigned_staff: [...newService.assigned_staff, member.id]
                                    });
                                  } else {
                                    setNewService({
                                      ...newService,
                                      assigned_staff: newService.assigned_staff.filter(id => id !== member.id)
                                    });
                                  }
                                }}
                                className="rounded text-blue-600"
                              />
                              <span className="text-sm">{member.name}</span>
                            </label>
                          ))
                        ) : (
                          <p className="text-xs text-gray-400 p-2">No staff members available. Add staff first.</p>
                        )}
                      </div>
                      {newService.assigned_staff.length > 0 && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {newService.assigned_staff.length} staff member(s) assigned
                        </p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (min)
                        </label>
                        <input
                          type="number"
                          value={newService.duration}
                          onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price (KD)
                        </label>
                        <input
                          type="number"
                          value={newService.price}
                          onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleAddService}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add Service
                    </button>
                    <button
                      onClick={() => {
                        setShowAddServiceModal(false);
                        setNewService({ name: '', duration: 30, price: 0, description: '', assigned_staff: [] });
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Edit Service Modal */}
            {showEditServiceModal && editingService && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-xl font-semibold mb-4">Edit Service</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Name *
                      </label>
                      <input
                        type="text"
                        value={editingService.name}
                        onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Deep Tissue Massage"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editingService.description || ''}
                        onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Describe the service..."
                      />
                    </div>
                    
                    {/* Staff Assignment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign Staff (Optional)
                      </label>
                      <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {staff.length > 0 ? (
                          staff.map((member) => (
                            <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(editingService.assigned_staff || []).includes(member.id)}
                                onChange={(e) => {
                                  const currentStaff = editingService.assigned_staff || [];
                                  if (e.target.checked) {
                                    setEditingService({
                                      ...editingService,
                                      assigned_staff: [...currentStaff, member.id]
                                    });
                                  } else {
                                    setEditingService({
                                      ...editingService,
                                      assigned_staff: currentStaff.filter(id => id !== member.id)
                                    });
                                  }
                                }}
                                className="rounded text-blue-600"
                              />
                              <span className="text-sm">{member.name}</span>
                            </label>
                          ))
                        ) : (
                          <p className="text-xs text-gray-400 p-2">No staff members available</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (min)
                        </label>
                        <input
                          type="number"
                          value={editingService.duration}
                          onChange={(e) => setEditingService({ ...editingService, duration: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price (KD)
                        </label>
                        <input
                          type="number"
                          value={editingService.price}
                          onChange={(e) => setEditingService({ ...editingService, price: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleUpdateService}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                    >
                      Update Service
                    </button>
                    <button
                      onClick={() => {
                        setShowEditServiceModal(false);
                        setEditingService(null);
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">{t('providerDashboard.staff')}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/provider/schedule')}
                  data-testid="open-daily-schedules-btn"
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {t('providerDashboard.dayBoard', 'Day Board')}
                </button>
                {subscriptionLimits && (
                  <span className="text-sm text-gray-600">
                    {t('providerDashboard.staff')} {staff.length}/{subscriptionLimits.staff_limit === -1 ? '∞' : subscriptionLimits.staff_limit}
                  </span>
                )}
                <button
                  onClick={() => {
                    if (subscriptionLimits && staff.length >= subscriptionLimits.staff_limit && subscriptionLimits.staff_limit !== -1) {
                      alert(`⚠️ Staff Limit Reached\n\nYou've reached your ${subscriptionLimits.tier_name} tier limit of ${subscriptionLimits.staff_limit} staff members.\n\nUpgrade your subscription to add more staff!`);
                      navigate('/pricing');
                    } else {
                      setShowAddStaffModal(true);
                    }
                  }}
                  disabled={subscriptionLimits && staff.length >= subscriptionLimits.staff_limit && subscriptionLimits.staff_limit !== -1}
                  className={`px-4 py-2 rounded-lg ${
                    subscriptionLimits && staff.length >= subscriptionLimits.staff_limit && subscriptionLimits.staff_limit !== -1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid="add-staff-btn"
                >
                  + {t('providerDashboard.addStaff', 'Add Staff')}
                </button>
              </div>
            </div>

            <div className="grid gap-3">
              {staff.map((member) => (
                <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="font-medium text-lg">{member.name}</span>
                      
                      {/* Display assigned services */}
                      {(() => {
                        const assignedServices = services.filter(service => 
                          service.assigned_staff && service.assigned_staff.includes(member.id)
                        );
                        return assignedServices.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">🎯 {t('providerDashboard.assignedServices', 'Assigned Services')}:</p>
                            <div className="flex flex-wrap gap-1">
                              {assignedServices.map(service => (
                                <span key={service.id} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  {service.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Display current schedule summary */}
                      {(() => {
                        const weekOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const days = weekOrder
                          .filter((day) => (staffSchedules[member.id] || {})[day]?.length > 0)
                          .map((day) => t(`days.${day}`));
                        return (
                          <div className="mt-2 text-sm text-gray-600">
                            <p className="text-xs" data-testid={`staff-availability-${member.id}`}>
                              📅 {days.length > 0
                                ? `${t('providerDashboard.staffAvailable', 'Available')}: ${days.join(isRTL ? '، ' : ', ')}`
                                : t('providerDashboard.staffFollowsOpeningHours', 'Follows the opening hours')}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedStaffForPerformance(member)}
                        className="text-emerald-700 hover:text-emerald-800 px-3 py-1 border border-emerald-300 hover:bg-emerald-50 rounded text-sm inline-flex items-center gap-1"
                        data-testid={`staff-performance-btn-${member.id}`}
                        title={t('providerDashboard.staffPerformanceHint', "View this staff member's monthly performance")}
                      >
                        📊 {t('providerDashboard.performance', 'Performance')}
                      </button>
                      <button
                        onClick={() => navigate(`/provider/schedule/list/${member.id}`)}
                        className="text-emerald-700 hover:text-emerald-900 px-3 py-1 border border-emerald-300 rounded text-sm font-semibold"
                        data-testid={`staff-today-schedule-btn-${member.id}`}
                      >
                        {t('providerDashboard.theirDay', 'Their Day')}
                      </button>
                      <button
                        onClick={() => setSelectedStaffForDaysOff(member)}
                        className="text-amber-600 hover:text-amber-800 px-3 py-1 border border-amber-300 rounded text-sm"
                        data-testid={`staff-days-off-btn-${member.id}`}
                      >
                        {t('providerDashboard.daysOff', 'Days Off')}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStaffForSchedule(member);
                          setShowScheduleModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 rounded text-sm"
                        data-testid={`staff-schedule-btn-${member.id}`}
                      >
                        {t('providerDashboard.schedule')}
                      </button>
                      <button
                        onClick={() => handleRemoveStaff(member.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        data-testid={`staff-remove-btn-${member.id}`}
                      >
                        {t('providerDashboard.removeStaff', 'Remove')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {staff.length === 0 && (
                <p className="text-center text-gray-500 py-8" data-testid="staff-empty-state">
                  {t('providerDashboard.noStaffYet', 'No staff members yet')}
                </p>
              )}
            </div>

            {/* Schedule Management Modal */}
            {showScheduleModal && selectedStaffForSchedule && (
              <StaffScheduleModal
                staff={selectedStaffForSchedule}
                schedule={staffSchedules[selectedStaffForSchedule.id] || {}}
                onSave={(schedule) => saveStaffSchedule(selectedStaffForSchedule.id, schedule)}
                onClose={() => {
                  setShowScheduleModal(false);
                  setSelectedStaffForSchedule(null);
                }}
              />
            )}

            {/* Days Off Modal */}
            {selectedStaffForDaysOff && (
              <StaffDaysOffModal
                staff={selectedStaffForDaysOff}
                providerId={providerId}
                sessionToken={sessionToken}
                onClose={() => setSelectedStaffForDaysOff(null)}
              />
            )}

            {/* Performance Modal (owner-only) */}
            {selectedStaffForPerformance && (
              <StaffPerformanceModal
                staff={selectedStaffForPerformance}
                onClose={() => setSelectedStaffForPerformance(null)}
              />
            )}

            {/* Add Staff Modal */}
            {showAddStaffModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <h3 className="text-xl font-semibold mb-4">Add Staff Member</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Staff Name *
                    </label>
                    <input
                      type="text"
                      value={newStaff.name}
                      onChange={(e) => setNewStaff({ name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter staff member's name"
                    />
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleAddStaff}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add Staff
                    </button>
                    <button
                      onClick={() => {
                        setShowAddStaffModal(false);
                        setNewStaff({ name: '' });
                      }}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-xl font-semibold">Appointments</h2>
              <CalendarExportToolbar sessionToken={sessionToken} />
            </div>
            
            {/* Filter buttons */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setAppointmentFilter('upcoming')}
                className={`px-4 py-2 rounded-lg ${appointmentFilter === 'upcoming' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setAppointmentFilter('completed')}
                className={`px-4 py-2 rounded-lg ${appointmentFilter === 'completed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Completed
              </button>
              <button
                onClick={() => setAppointmentFilter('cancelled')}
                className={`px-4 py-2 rounded-lg ${appointmentFilter === 'cancelled' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Cancelled
              </button>
            </div>
            
            {/* Appointments list */}
            <div className="space-y-4">
              {appointments
                .filter(apt => {
                  if (appointmentFilter === 'upcoming') return apt.status === 'scheduled' || apt.status === 'confirmed';
                  if (appointmentFilter === 'completed') return apt.status === 'completed';
                  if (appointmentFilter === 'cancelled') return apt.status === 'cancelled';
                  return true;
                })
                .sort((a, b) => {
                  // Sort by date first, then by time
                  const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
                  const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
                  return dateA - dateB; // Earlier dates/times first
                })
                .map((appointment) => (
                  <div key={appointment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{appointment.patient_name}</h3>
                        <p className="text-sm text-gray-600">{appointment.service_name}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          📅 {appointment.appointment_date} at {appointment.appointment_time}
                        </p>
                        {appointment.staff_name && (
                          <p className="text-sm text-gray-500">👤 Staff: {appointment.staff_name}</p>
                        )}
                        <p className="text-sm text-gray-500">💰 {appointment.consultation_fee} KD - {appointment.payment_method}</p>
                        {appointment.symptoms && (
                          <p className="text-sm text-gray-600 mt-2">📝 {appointment.symptoms}</p>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          appointment.status === 'scheduled' || appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {appointment.status}
                        </span>
                        
                        {appointment.status === 'confirmed' && (
                          <AddToCalendar appointment={appointment} compact={true} />
                        )}
                        
                        {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => openRescheduleModal(appointment)}
                              className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                            >
                              Reschedule
                            </button>
                            <button
                              onClick={() => handleCompleteAppointment(appointment.id)}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Mark Complete
                            </button>
                            <button
                              onClick={() => handleCancelAppointment(appointment.id)}
                              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              
              {appointments.filter(apt => {
                if (appointmentFilter === 'upcoming') return apt.status === 'scheduled' || apt.status === 'confirmed';
                if (appointmentFilter === 'completed') return apt.status === 'completed';
                if (appointmentFilter === 'cancelled') return apt.status === 'cancelled';
                return true;
              }).length === 0 && (
                <p className="text-gray-500 text-center py-8">No {appointmentFilter} appointments</p>
              )}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="bg-white rounded-lg shadow">
            {loadingSubscription ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('providerDashboard.subscription.loading', 'Loading subscription details...')}</p>
              </div>
            ) : !subscriptionData?.subscription ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">{t('providerDashboard.subscription.noData', 'Unable to load subscription data')}</p>
                {subscriptionData?.error && (
                  <p className="text-red-500 text-sm mt-2">{subscriptionData.error}</p>
                )}
                <button 
                  onClick={fetchSubscriptionData}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {t('providerDashboard.subscription.retry', 'Retry')}
                </button>
              </div>
            ) : (
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-6">{t('providerDashboard.subscription.title', 'Subscription Management')}</h2>

                {/* Approaching-limit nudge (>=80% of staff or services on a non-unlimited tier) */}
                {subscriptionLimits && (() => {
                  const sLim = subscriptionLimits.staff_limit;
                  const svcLim = subscriptionLimits.services_limit;
                  const sNear = sLim > 0 && (subscriptionLimits.current_staff / sLim) >= 0.8;
                  const svcNear = svcLim > 0 && (subscriptionLimits.current_services / svcLim) >= 0.8;
                  if (!sNear && !svcNear) return null;
                  const resource = sNear && svcNear
                    ? t('providerDashboard.subscription.maxStaff', 'Max Staff') + ' & ' + t('providerDashboard.subscription.maxServices', 'Max Services').toLowerCase()
                    : sNear ? t('providerDashboard.subscription.maxStaff', 'Max Staff').toLowerCase()
                            : t('providerDashboard.subscription.maxServices', 'Max Services').toLowerCase();
                  const used = sNear ? subscriptionLimits.current_staff : subscriptionLimits.current_services;
                  const limit = sNear ? sLim : svcLim;
                  return (
                    <div
                      className="flex flex-wrap items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 mb-6"
                      data-testid="near-limit-nudge"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold flex items-center gap-2">
                          <span aria-hidden>⚡</span>
                          {t('providerDashboard.subscription.almostAtLimit', "You're almost at your plan limit")}
                        </p>
                        <p className="text-sm mt-0.5">
                          {t('providerDashboard.subscription.almostAtLimitDesc', "Upgrade now to keep growing — you've used {{used}} of {{limit}} {{resource}}.", { used, limit, resource })}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-lg shrink-0"
                        data-testid="near-limit-upgrade-btn"
                      >
                        {t('providerDashboard.subscription.upgradeNow', 'Upgrade now')}
                      </button>
                    </div>
                  );
                })()}

                {/* Show error message if any */}
                {subscriptionData?.error && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-6">
                    <p className="text-sm">Note: {subscriptionData.error}</p>
                  </div>
                )}
                
                {/* Current Plan Card */}
                <div className={`rounded-xl p-6 mb-6 border-2 ${
                  subscriptionData.subscription?.current_tier === 'Free' 
                    ? 'bg-gray-50 border-gray-200' 
                    : subscriptionData.subscription?.current_tier === 'Basic'
                    ? 'bg-blue-50 border-blue-200'
                    : subscriptionData.subscription?.current_tier === 'Pro'
                    ? 'bg-purple-50 border-purple-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">{t('providerDashboard.subscription.currentPlan', 'Current Plan')}</p>
                      <h3 className={`text-3xl font-bold ${
                        subscriptionData.subscription?.current_tier === 'Free' 
                          ? 'text-gray-700' 
                          : subscriptionData.subscription?.current_tier === 'Basic'
                          ? 'text-blue-700'
                          : subscriptionData.subscription?.current_tier === 'Pro'
                          ? 'text-purple-700'
                          : 'text-amber-700'
                      }`}>
                        {subscriptionData.subscription?.current_tier || 'Free'}
                      </h3>
                      {subscriptionData.subscription?.commission_rate && (
                        <p className="text-sm text-gray-600 mt-1">
                          {t('providerDashboard.subscription.commission', 'Commission Rate')}: {subscriptionData.subscription.commission_rate}%
                        </p>
                      )}
                    </div>
                    
                    {subscriptionData.subscription?.current_tier !== 'Free' && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        subscriptionData.subscription?.is_expired
                          ? 'bg-red-100 text-red-700'
                          : subscriptionData.subscription?.days_remaining <= 7
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {subscriptionData.subscription?.is_expired 
                          ? t('providerDashboard.subscription.expired', 'Expired')
                          : t('providerDashboard.subscription.active', 'Active')
                        }
                      </span>
                    )}
                  </div>

                  {/* Expiry Info */}
                  {subscriptionData.subscription?.current_tier !== 'Free' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">{t('providerDashboard.subscription.renewalDate', 'Renewal Date')}</p>
                          <p className="font-semibold text-gray-800">
                            {subscriptionData.subscription?.expires_at 
                              ? new Date(subscriptionData.subscription.expires_at).toLocaleDateString()
                              : '-'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">{t('providerDashboard.subscription.daysRemaining', 'Days Remaining')}</p>
                          <p className={`font-semibold ${
                            subscriptionData.subscription?.is_expired
                              ? 'text-red-600'
                              : subscriptionData.subscription?.days_remaining <= 7
                              ? 'text-amber-600'
                              : 'text-green-600'
                          }`}>
                            {subscriptionData.subscription?.is_expired 
                              ? t('providerDashboard.subscription.expiredText', 'Expired')
                              : `${subscriptionData.subscription?.days_remaining || 0} ${t('providerDashboard.subscription.days', 'days')}`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Plan Limits */}
                {subscriptionData.subscription?.limits && (
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h4 className="font-semibold text-gray-800 mb-4">{t('providerDashboard.subscription.planLimits', 'Plan Limits')}</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-blue-600" data-testid="plan-limit-max-staff">
                          {subscriptionData.subscription.limits.max_staff === -1 ? '∞' : subscriptionData.subscription.limits.max_staff}
                        </p>
                        <p className="text-sm text-gray-500">{t('providerDashboard.subscription.maxStaff', 'Max Staff')}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-green-600" data-testid="plan-limit-max-services">
                          {subscriptionData.subscription.limits.max_services === -1 ? '∞' : subscriptionData.subscription.limits.max_services}
                        </p>
                        <p className="text-sm text-gray-500">{t('providerDashboard.subscription.maxServices', 'Max Services')}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg border">
                        <p className="text-2xl font-bold text-purple-600" data-testid="plan-limit-booking-window">
                          {subscriptionData.subscription.limits.booking_window_months}
                        </p>
                        <p className="text-sm text-gray-500">{t('providerDashboard.subscription.bookingWindow', 'Booking Window (Months)')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-4">
                  {/* View Upgrade Plans (replaces former "Contact Support") */}
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
                    data-testid="view-upgrade-plans-btn"
                  >
                    {t('providerDashboard.subscription.viewUpgradePlans', 'View Upgrade Plans')}
                  </button>

                  {/* Cancel Subscription */}
                  {subscriptionData.subscription?.current_tier !== 'Free' && (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancellingSubscription}
                      className="flex-1 border-2 border-red-300 text-red-600 py-3 px-6 rounded-lg font-semibold hover:bg-red-50 transition-all disabled:opacity-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancellingSubscription
                        ? t('providerDashboard.subscription.cancelling', 'Cancelling...')
                        : t('providerDashboard.subscription.cancelSubscription', 'Cancel Subscription')
                      }
                    </button>
                  )}
                </div>

                {/* Subscription History */}
                {subscriptionData.tier_history && subscriptionData.tier_history.length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-gray-800 mb-4">{t('providerDashboard.subscription.history', 'Subscription History')}</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('providerDashboard.subscription.tier', 'Tier')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('providerDashboard.subscription.changedAt', 'Changed At')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('providerDashboard.subscription.reason', 'Reason')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {subscriptionData.tier_history.map((entry, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  entry.tier_name === 'Free' ? 'bg-gray-100 text-gray-700' :
                                  entry.tier_name === 'Basic' ? 'bg-blue-100 text-blue-700' :
                                  entry.tier_name === 'Pro' ? 'bg-purple-100 text-purple-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {entry.tier_name}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {entry.changed_at ? new Date(entry.changed_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {entry.reason === 'subscription_expired' ? t('providerDashboard.subscription.reasonExpired', 'Subscription Expired') :
                                 entry.reason === 'user_cancelled' ? t('providerDashboard.subscription.reasonCancelled', 'User Cancelled') :
                                 entry.reason === 'admin_update' ? t('providerDashboard.subscription.reasonAdminUpdate', 'Admin Update') :
                                 entry.changed_by === 'admin' ? t('providerDashboard.subscription.reasonAdminChange', 'Admin Change') :
                                 t('providerDashboard.subscription.reasonUpgrade', 'Plan Change')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Upgrade Options Preview */}
                {subscriptionData.upgrade_options && subscriptionData.upgrade_options.length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-gray-800 mb-4">{t('providerDashboard.subscription.availableUpgrades', 'Available Upgrades')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[...subscriptionData.upgrade_options]
                        .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
                        .map((option) => (
                        <div key={option.tier_name} className="border rounded-xl p-5 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-3">
                            <h5 className="font-bold text-lg text-gray-800">{option.tier_name}</h5>
                            <span className="text-purple-600 font-bold">
                              {option.is_custom_pricing
                                ? t('providerDashboard.subscription.customPricing', 'Custom pricing')
                                : `${option.monthly_price} KD/mo`}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{option.commission_rate}% {t('providerDashboard.subscription.commission', 'commission')}</p>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• {t('providerDashboard.subscription.maxStaff', 'Max Staff')}: {option.max_staff === -1 ? t('providerDashboard.subscription.unlimited', 'Unlimited') : option.max_staff}</li>
                            <li>• {t('providerDashboard.subscription.maxServices', 'Max Services')}: {option.max_services === -1 ? t('providerDashboard.subscription.unlimited', 'Unlimited') : option.max_services}</li>
                          </ul>
                          <button
                            onClick={() => navigate('/pricing')}
                            className="w-full mt-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors"
                          >
                            {t('providerDashboard.subscription.selectPlan', 'Select Plan')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reschedule Modal */}
        {showRescheduleModal && rescheduleAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">Reschedule Appointment</h3>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">User:</span> {rescheduleAppointment.patient_name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Service:</span> {rescheduleAppointment.service_name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Duration:</span> {rescheduleAppointment.duration || 30} mins
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Current:</span> {rescheduleAppointment.appointment_date} at {rescheduleAppointment.appointment_time}
                </p>
                {rescheduleAppointment.staff_id && (
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Current Staff:</span> {staff.find(s => s.id === rescheduleAppointment.staff_id)?.name || 'N/A'}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {/* Staff Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  {staff.length === 0 ? (
                    <div className="w-full px-3 py-2 text-amber-600 text-sm bg-amber-50 rounded-lg">
                      No staff members available. Please add staff in the Staff tab.
                    </div>
                  ) : (
                    <select
                      value={rescheduleData.staffId}
                      onChange={(e) => handleRescheduleStaffChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select staff</option>
                      {staff.filter(s => {
                        // Only show staff that can perform this service
                        const service = services.find(svc => svc.id === rescheduleAppointment.service_id);
                        // If no assigned_staff array or it's empty, show all staff
                        return !service?.assigned_staff?.length || service.assigned_staff.includes(s.id);
                      }).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Date *
                  </label>
                  <input
                    type="date"
                    value={rescheduleData.date}
                    onChange={(e) => handleRescheduleDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                {/* Time Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Time *
                  </label>
                  {loadingRescheduleSlots ? (
                    <div className="w-full px-3 py-2 text-gray-500 text-sm">Loading available slots...</div>
                  ) : !rescheduleData.staffId ? (
                    <div className="w-full px-3 py-2 text-amber-600 text-sm bg-amber-50 rounded-lg">
                      Please select a staff member first
                    </div>
                  ) : !rescheduleData.date ? (
                    <div className="w-full px-3 py-2 text-amber-600 text-sm bg-amber-50 rounded-lg">
                      Please select a date first
                    </div>
                  ) : rescheduleAvailableSlots.length === 0 ? (
                    <div className="w-full px-3 py-2 text-red-600 text-sm bg-red-50 rounded-lg">
                      No available slots for this staff member on this date. The staff may be off or fully booked.
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-1">
                      {rescheduleAvailableSlots.map(time => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setRescheduleData(prev => ({ ...prev, time }))}
                          className={`px-2 py-1.5 text-sm rounded-lg border transition-all ${
                            rescheduleData.time === time
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  )}
                  {rescheduleAvailableSlots.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {rescheduleAvailableSlots.length} available slot(s)
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleRescheduleAppointment(
                    rescheduleAppointment.id, 
                    rescheduleData.date, 
                    rescheduleData.time,
                    rescheduleData.staffId
                  )}
                  disabled={!rescheduleData.date || !rescheduleData.time || !rescheduleData.staffId}
                  className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Reschedule
                </button>
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleAppointment(null);
                    setRescheduleAvailableSlots([]);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImprovedProviderDashboard;
